/**
 * FloatingEdge — an edge that dynamically connects from whichever side of each node
 * is closest to the other node. Requires nodes to have handles on all 4 sides.
 *
 * Used in FocusedSubgraph (call graph) and ImportFocusedSubgraph (import graph)
 * so that manually rearranged nodes always get clean edge routing.
 *
 * Issue #604
 */

import { useInternalNode, getSmoothStepPath, type EdgeProps, Position } from '@xyflow/react';

/** Given a node's absolute position + size and the other node's center, return
 *  the attachment point (absolute coords) and the handle Position. */
function getAttachmentPoint(
  nodeX: number,
  nodeY: number,
  width: number,
  height: number,
  otherCx: number,
  otherCy: number,
): { x: number; y: number; position: Position } {
  const cx = nodeX + width / 2;
  const cy = nodeY + height / 2;
  const dx = otherCx - cx;
  const dy = otherCy - cy;

  // Determine which side the other node is on relative to this node
  if (Math.abs(dx) >= Math.abs(dy)) {
    // Predominantly horizontal
    if (dx >= 0) return { x: nodeX + width, y: cy, position: Position.Right };
    return { x: nodeX, y: cy, position: Position.Left };
  }
  // Predominantly vertical
  if (dy >= 0) return { x: cx, y: nodeY + height, position: Position.Bottom };
  return { x: cx, y: nodeY, position: Position.Top };
}

export interface FloatingEdgeData extends Record<string, unknown> {
  colour?: string;
}

/** Drop-in replacement for the default edge that routes from the nearest side. */
export function FloatingEdge({
  id,
  source,
  target,
  markerEnd,
  style,
  label,
  labelStyle,
  animated,
}: EdgeProps) {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);

  if (!sourceNode || !targetNode) return null;

  const sx = sourceNode.internals.positionAbsolute.x;
  const sy = sourceNode.internals.positionAbsolute.y;
  const sw = sourceNode.measured?.width ?? 150;
  const sh = sourceNode.measured?.height ?? 50;

  const tx = targetNode.internals.positionAbsolute.x;
  const ty = targetNode.internals.positionAbsolute.y;
  const tw = targetNode.measured?.width ?? 150;
  const th = targetNode.measured?.height ?? 50;

  const targetCx = tx + tw / 2;
  const targetCy = ty + th / 2;
  const sourceCx = sx + sw / 2;
  const sourceCy = sy + sh / 2;

  const srcAttach = getAttachmentPoint(sx, sy, sw, sh, targetCx, targetCy);
  const tgtAttach = getAttachmentPoint(tx, ty, tw, th, sourceCx, sourceCy);

  const [edgePath] = getSmoothStepPath({
    sourceX: srcAttach.x,
    sourceY: srcAttach.y,
    sourcePosition: srcAttach.position,
    targetX: tgtAttach.x,
    targetY: tgtAttach.y,
    targetPosition: tgtAttach.position,
    borderRadius: 8,
  });

  return (
    <>
      <path
        id={id}
        className={`react-flow__edge-path${animated ? ' animated' : ''}`}
        d={edgePath}
        style={style}
        markerEnd={markerEnd as string | undefined}
      />
      {label && (
        <text>
          <textPath
            href={`#${id}`}
            startOffset="50%"
            textAnchor="middle"
            style={labelStyle as React.CSSProperties}
          >
            {label as string}
          </textPath>
        </text>
      )}
    </>
  );
}
